import { BanIcon, CheckIcon, MoveIcon, Star, UsersIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RhythmDisplay } from '@/components/forms/rhythm-fields';
import { InlineEditInput } from '@/components/inline-edit/inline-edit-input';
import { PhysicalAccessKeyIcon } from '@/components/physical-access-key-icon';
import { PriorityBadge } from '@/components/priority-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { INSTALLABLE_STAR_CLASSNAME, TONES } from '@/lib/tones';
import { cn } from '@/lib/utils';
import {
  SlotSheetDefinitionList,
  SlotSheetRow,
  SlotSheetSection,
} from '../../slot-sheet/slot-sheet-section';
import { ViewAdminAgendaSlotSheetViewModel } from '../view-admin-agenda-slot-sheet.viewmodel';

type Reservation = NonNullable<
  ViewAdminAgendaSlotSheetViewModel['reservation']
>['reservations'][number];

/**
 * Reservation card: who positioned on the slot, how to reach them, their rhythm
 * and — while the request is pending — the accept/decline actions.
 *
 * @param props.reservation - one reservation entry of the sheet view model
 * @param props.canEdit - whether the viewer may act on the reservation
 * @returns the reservation card element
 */
const ReservationCard = ({
  reservation,
  canEdit,
}: {
  reservation: Reservation;
  canEdit: boolean;
}) => {
  const { t } = useTranslation();
  const { data, api } = reservation;

  return (
    <article className="overflow-hidden rounded-lg border bg-card">
      <header className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
        <span
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-full font-semibold text-[11px]',
            TONES.amber.pill,
            TONES.amber.text,
          )}
          aria-hidden="true"
        >
          {data.doctorSubstituteInitials}
        </span>

        <p className="min-w-0 flex-1 truncate font-medium text-sm">
          {t(data.doctorSubstitute.key, data.doctorSubstitute.params)}
        </p>

        {data.doctorSubstituteInstallable && (
          <Star
            className={cn('size-3.5 shrink-0', INSTALLABLE_STAR_CLASSNAME)}
            aria-label={t('agenda-slot-calendar-event.installable-badge')}
          />
        )}
        <PhysicalAccessKeyIcon
          granted={data.doctorSubstituteHasPhysicalSiteAccess}
        />
        {data.doctorSubstitutePriority != null && (
          <PriorityBadge priority={data.doctorSubstitutePriority} />
        )}
        <Badge
          className={cn('text-xs', data.status.badgeClassName)}
          variant="outline"
        >
          {t(data.status.label)}
        </Badge>
      </header>

      <div className="px-3 py-3">
        <SlotSheetDefinitionList>
          {data.doctorSubstitutePhoneNumber && (
            <SlotSheetRow
              label={t('view-admin-agenda-slot-sheet.reservation.phone-number')}
            >
              <a
                href={`tel:${data.doctorSubstitutePhoneNumber.replace(/\s/g, '')}`}
                className="tabular-nums hover:underline"
              >
                {data.doctorSubstitutePhoneNumber}
              </a>
            </SlotSheetRow>
          )}

          {(data.doctorSubstituteOnSiteSlotsWithoutAssistant != null ||
            data.doctorSubstituteOnSiteSlotsWithAssistant != null) && (
            <SlotSheetRow
              label={t(
                'view-admin-agenda-slot-sheet.reservation.doctor-rhythm',
              )}
              alignTop
            >
              <RhythmDisplay
                onSiteSlotsWithoutAssistant={
                  data.doctorSubstituteOnSiteSlotsWithoutAssistant
                }
                remoteSlotsWithoutAssistant={
                  data.doctorSubstituteRemoteSlotsWithoutAssistant
                }
                onSiteSlotsWithAssistant={
                  data.doctorSubstituteOnSiteSlotsWithAssistant
                }
                remoteSlotsWithAssistant={
                  data.doctorSubstituteRemoteSlotsWithAssistant
                }
                rhythmNote={data.doctorSubstituteRhythmNote}
              />
            </SlotSheetRow>
          )}

          <SlotSheetRow
            label={t('view-admin-agenda-slot-sheet.reservation.tag.label')}
          >
            {canEdit ? (
              <InlineEditInput
                value={data.tag || ''}
                onSave={(value) => api.updateTag(value || undefined)}
                isLoading={data.updateTagLoading}
                placeholder={t(
                  'view-admin-agenda-slot-sheet.reservation.tag.placeholder',
                )}
                maxLength={10}
                inputClassName="h-8 w-32"
              />
            ) : (
              <span>{data.tag || '-'}</span>
            )}
          </SlotSheetRow>
        </SlotSheetDefinitionList>
      </div>

      {canEdit && data.status.pending && (
        <footer className="flex flex-col gap-2 border-t bg-muted/30 p-2">
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-destructive"
              onClick={api.decline}
              loading={data.declineLoading}
            >
              <BanIcon
                className="size-4"
                aria-hidden="true"
              />
              {t('btn.decline')}
            </Button>
            <Button
              size="sm"
              onClick={api.confirm}
              loading={data.confirmLoading}
            >
              <CheckIcon
                className="size-4"
                aria-hidden="true"
              />
              {t('btn.confirm')}
            </Button>
          </div>
          {data.canMove && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={api.move}
            >
              <MoveIcon
                className="size-4"
                aria-hidden="true"
              />
              {t('view-admin-agenda-slot-sheet.btn.move-slot')}
            </Button>
          )}
        </footer>
      )}
    </article>
  );
};

/**
 * "Candidatures" / "Réservation" section: every substitute positioned on the
 * slot, ordered as the API returns them (by priority).
 *
 * @param props.reservation - the reservation branch of the sheet view model
 * @param props.canEdit - whether the viewer may act on the reservations
 * @returns the section element
 */
export const ViewAdminAgendaSlotSectionReservations = ({
  reservation,
  canEdit,
}: {
  reservation: NonNullable<ViewAdminAgendaSlotSheetViewModel['reservation']>;
  canEdit: boolean;
}) => {
  const { t } = useTranslation();

  const hasPendingRequest = reservation.reservations.some(
    (entry) => entry.data.status.pending,
  );

  return (
    <SlotSheetSection
      icon={UsersIcon}
      title={
        <>
          {hasPendingRequest
            ? t('slot-sheet.section.candidates')
            : t('slot-sheet.section.reservation')}
          <span className="text-muted-foreground tabular-nums">
            ({reservation.reservations.length})
          </span>
        </>
      }
      trailing={
        hasPendingRequest ? (
          <span className="text-muted-foreground text-xs">
            {t('slot-sheet.sorted-by-priority')}
          </span>
        ) : null
      }
    >
      <ul className="flex flex-col gap-2.5">
        {reservation.reservations.map((entry) => (
          <li key={entry.data.id}>
            <ReservationCard
              reservation={entry}
              canEdit={canEdit}
            />
          </li>
        ))}
      </ul>
    </SlotSheetSection>
  );
};
